// src/lib/session.client.ts — sesiunea curentă în browser (user + memberships),
// hidratată din GET /api/auth/me. Persistă cât ține cookie-ul httpOnly (server-side);
// aici ținem doar oglinda reactivă pentru UI (header, gating de UI).

import van from "vanjs-core"
import { api_get, api_patch, api_post } from "./api.client"
import {
	ALL_FEATURES,
	ALL_ACTIONS,
	type FeatureFlag,
	type FeatureSet,
	type ActionRight,
	type ActionSet,
} from "./entitlements"

export interface SessionMembership {
	org_id: string
	org_slug: string
	org_name: string
	role: string
	// Drepturile pe funcționalități ale org-ului (bool-uri EFECTIVE, calculate pe server) —
	// UI-ul ascunde ce e oprit; refuzul real e pe server. Poate lipsi pe sesiuni vechi → ALL.
	features?: FeatureSet
	// Departamentul membrului în org (4.4, text liber, opțional) — filtrul „Departamentul meu" din
	// inbox; poate lipsi pe sesiuni vechi.
	department?: string | null
	// Drepturile pe ACȚIUNI (Lotul 2: export/import/partajare/stergere) ale membrului — UI-ul ascunde
	// butoanele; refuzul real e pe server. Poate lipsi pe sesiuni vechi → toate permise.
	actions?: ActionSet
}
export interface SessionUser {
	id: string
	email: string
	display_name: string
	platform_role: "super" | "support" | null
	/** Are parolă locală; `false` = cont doar-SSO → ascundem schimbarea parolei. */
	has_password: boolean
}

/** Numele de afișat: numele setat, altfel partea din stânga a emailului (conturi vechi). */
export function user_label(u: SessionUser | null): string {
	if (!u) {
		return ""
	}
	return u.display_name.trim() || u.email.split("@")[0]
}

/** Inițialele pentru avatar (max 2 litere), derivate din numele de afișat. */
export function user_initials(u: SessionUser | null): string {
	const parts = user_label(u)
		.split(/[\s._-]+/)
		.filter(Boolean)
	if (parts.length === 0) {
		return "?"
	}
	const letters = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[1][0]
	return letters.toUpperCase()
}
export interface Session {
	authenticated: boolean
	user: SessionUser | null
	memberships: SessionMembership[]
}

const ANON: Session = { authenticated: false, user: null, memberships: [] }
export const session = van.state<Session>(ANON)

// ── Drepturi pe funcționalități (gating de UI; refuzul real e pe server) ──
// Implicit TRUE când org-ul nu e în memberships (anonim, super pe org străin, sesiune veche):
// ascundem DOAR ce știm sigur că e oprit — un buton în plus dă 403 curat, unul lipsă derutează.

/** Flag-urile org-ului (după id SAU slug) din sesiunea curentă. */
export function session_org_features(org: string): FeatureSet {
	const m = session.val.memberships.find((x) => x.org_id === org || x.org_slug === org)
	return m?.features ?? ALL_FEATURES
}

export function session_org_feature(org: string, flag: FeatureFlag): boolean {
	return session_org_features(org)[flag]
}

/** Acțiunea (Lotul 2) e permisă pe org-ul dat (după id SAU slug)? Org absent din sesiune → permis (UI). */
export function session_org_action(org: string, action: ActionRight): boolean {
	const m = session.val.memberships.find((x) => x.org_id === org || x.org_slug === org)
	return (m?.actions ?? ALL_ACTIONS)[action]
}

/** Are VREUN org al userului flag-ul activ? (nav global, fără context de org). */
export function session_any_feature(flag: FeatureFlag): boolean {
	const ms = session.val.memberships
	if (ms.length === 0) {
		return true
	}
	return ms.some((m) => (m.features ?? ALL_FEATURES)[flag])
}
// Devine true după prima hidratare din /api/auth/me — ca UI-ul să nu decidă „anonim" prematur.
export const session_loaded = van.state(false)

/** Reîncarcă sesiunea din server (cookie). 401 → anonim. */
export async function load_session(): Promise<void> {
	const r = await api_get<{ user: SessionUser; memberships: SessionMembership[] }>("/api/auth/me")
	session.val =
		r.ok && r.data?.user
			? { authenticated: true, user: r.data.user, memberships: r.data.memberships ?? [] }
			: ANON
	session_loaded.val = true
}

/** „Am uitat parola": răspunsul e generic (serverul nu spune dacă emailul există); 501 = fără mail pe instanță. */
export async function forgot_password(email: string): Promise<{ ok: boolean; message?: string }> {
	const r = await api_post("/api/auth/password/forgot", { email })
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}

/** Parolă nouă cu tokenul din email. */
export async function reset_password(
	token: string,
	password: string,
): Promise<{ ok: boolean; message?: string }> {
	const r = await api_post("/api/auth/password/reset", { token, password })
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}

/** Pre-verificarea linkului de resetare (fără scriere) — pagina o cheamă la deschidere. */
export async function check_reset_token(token: string): Promise<{ ok: boolean; message?: string }> {
	const r = await api_post("/api/auth/password/reset/check", { token })
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}

export async function login(
	email: string,
	password: string,
): Promise<{ ok: boolean; message?: string }> {
	const r = await api_post("/api/auth/login", { email, password })
	if (r.ok) {
		await load_session()
	}
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}

export async function register(
	email: string,
	password: string,
	display_name: string,
): Promise<{ ok: boolean; message?: string }> {
	const r = await api_post("/api/auth/register", { email, password, display_name })
	if (r.ok) {
		await load_session()
	}
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}

export async function logout(): Promise<void> {
	await api_post("/api/auth/logout", {})
	session.val = ANON
}

/** Actualizează numele afișat + re-hidratează sesiunea (headerul se re-randează singur). */
export async function update_profile(
	display_name: string,
): Promise<{ ok: boolean; message?: string }> {
	const r = await api_patch("/api/auth/me", { display_name })
	if (r.ok) {
		await load_session()
	}
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}

/** Schimbă parola proprie (cere parola curentă). Sesiunea rămâne validă. */
export async function change_password(
	current_password: string,
	new_password: string,
): Promise<{ ok: boolean; message?: string }> {
	const r = await api_post("/api/auth/password", { current_password, new_password })
	return { ok: r.ok, message: r.ok ? undefined : r.err?.message }
}
